import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./globals.css";
import { initFontPreloading } from "@/lib/fontPreloader";
import { applyCanonicalFont, preloadTitilliumFont } from "@/lib/fontGovernance";
import { debugLog, debugWarn } from "@/lib/debugLog";
import { detectDeviceProfile } from "@/lib/deviceProfile";
import * as ActiveCartPreference from "@/lib/activeCartPreference";

if (import.meta.env.PROD) {
	console.log = () => {};
	console.debug = () => {};
	console.info = () => {};
}

declare global {
	interface Window {
		__BOOT_ACTIVE_SHARED_CART_THEME_PRIORITY__?: boolean;
	}
}

// PERFORMANCE: Initialize font governance immediately (cold start optimization)
// Titillium Web is the CANONICAL and ONLY font for the entire app
applyCanonicalFont();
preloadTitilliumFont();
initFontPreloading();

// Device adaptation: constrain effects for Kindle/Web Explorer-class browsers.
const deviceProfile = detectDeviceProfile();
if (deviceProfile.isKindleLite) {
	document.documentElement.classList.add('kindle-lite');
}

// Bootstrap bridge: detect shared-cart priority before React mounts and notify immediately.
if (typeof ActiveCartPreference.initializeActiveCartTruthManagerSignals === 'function') {
	ActiveCartPreference.initializeActiveCartTruthManagerSignals();
}
const bootSharedCartThemePriority = ActiveCartPreference.getActiveSharedCartThemePriority();
window.__BOOT_ACTIVE_SHARED_CART_THEME_PRIORITY__ = bootSharedCartThemePriority;
window.dispatchEvent(new CustomEvent(ActiveCartPreference.ACTIVE_SHARED_CART_THEME_PRIORITY_CHANGED_EVENT, {
	detail: {
		isEnabled: bootSharedCartThemePriority,
		phase: 'pre-react-mount',
	},
}));

// Make root visible once React mounts
const rootElement = document.getElementById("root");
if (rootElement) {
  const observer = new MutationObserver(() => {
    // React has rendered - make visible
    document.body.style.visibility = 'visible';
    rootElement.style.visibility = 'visible';
    observer.disconnect();
  });
  
  observer.observe(rootElement, {
    childList: true,
    subtree: true,
  });
}

// Register Service Worker only in production to avoid dev HMR/cache conflicts.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
	window.addEventListener('load', () => {
		navigator.serviceWorker.getRegistrations()
			.then((registrations) => {
				registrations.forEach((registration) => {
					const scriptURL =
						registration.active?.scriptURL ||
						registration.waiting?.scriptURL ||
						registration.installing?.scriptURL ||
						'';

					if (scriptURL.includes('/service-worker.js')) {
						debugLog('sw', 'Desregistrando Service Worker legacy:', scriptURL);
						void registration.unregister();
					}
				});
			})
			.catch((error) => {
				debugWarn('sw', 'No se pudieron leer registros de Service Worker:', error);
			});

		navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
			.then(reg => {
				debugLog('sw', 'Service Worker registrado:', reg);

				const signalNewVersion = async () => {
					try {
						if (reg.waiting) {
							reg.waiting.postMessage({ type: 'SKIP_WAITING' });
							return;
						}
						await reg.update();
					} catch (error) {
						debugWarn('sw', 'Error enviando señal de nueva versión:', error);
					}
				};

				const handleForceUpdateSignal = () => {
					debugLog('sw', 'Señal manual recibida: sw-force-update');
					void signalNewVersion();
				};

				window.addEventListener('sw-force-update', handleForceUpdateSignal as EventListener);

				const dispatchUpdateAvailable = (waitingWorker?: ServiceWorker | null) => {
					const updateInProgress = sessionStorage.getItem('sw-update-in-progress');
					const alreadyShown = sessionStorage.getItem('sw-update-shown');
					const lastDispatchAt = Number(sessionStorage.getItem('sw-update-last-dispatch-at') || '0');
					const now = Date.now();
					if (updateInProgress) return;
					if (alreadyShown) return;
					// Throttle duplicate dispatches from repeated updatefound/install cycles.
					if (lastDispatchAt && now - lastDispatchAt < 15000) return;

					sessionStorage.setItem('sw-update-last-dispatch-at', String(now));
					window.dispatchEvent(new CustomEvent('sw-update-available', {
						detail: {
							registration: reg,
							waitingWorker: waitingWorker || reg.waiting || null,
						},
					}));
				};
				
				// Revisar actualizaciones cada 60 segundos
				setInterval(() => {
					reg.update();
				}, 60000);

				// Si ya hay una versión waiting al cargar, avisar de inmediato
				if (reg.waiting && navigator.serviceWorker.controller) {
					dispatchUpdateAvailable(reg.waiting);
				}

				// Señal explícita al worker al iniciar: aplica nueva versión si ya está waiting,
				// o fuerza chequeo de update en caso contrario.
				void signalNewVersion();
				
				// Detectar cuando hay una nueva versión disponible
				reg.addEventListener('updatefound', () => {
					const newWorker = reg.installing;
					newWorker?.addEventListener('statechange', () => {
						if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
							debugLog('sw', 'Nueva versión disponible. Mostrando toast al usuario.');
							dispatchUpdateAvailable(newWorker);
						}
					});
				});

				window.addEventListener('beforeunload', () => {
					window.removeEventListener('sw-force-update', handleForceUpdateSignal as EventListener);
				}, { once: true });
			})
			.catch(err => {
				debugWarn('sw', 'Error registrando Service Worker:', err);
			});
	});
}

createRoot(document.getElementById("root")!).render(<App />);

// Re-dispatch right after mount cycle so mounted React listeners receive the bootstrap state.
queueMicrotask(() => {
	window.dispatchEvent(new CustomEvent(ActiveCartPreference.ACTIVE_SHARED_CART_THEME_PRIORITY_CHANGED_EVENT, {
		detail: {
			isEnabled: bootSharedCartThemePriority,
			phase: 'post-react-mount',
		},
	}));
});